<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useCurrency } from '@/composables/currency'
import { useNumberComparison } from '@/composables/number-comparison'

import KpiMetricTile from '@/components/dashboard/charts/KpiMetricTile.vue'
import ExpandableContent from '@/components/shared/ExpandableContent.vue'

const props = defineProps({
  company: {
    type: Object,
    required: true,
  },
  variant: {
    type: String,
    default: 'tiles',
  },
})

const { t } = getI18n()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const { getCurrencyString } = useCurrency()
const { isGreater, isLess } = useNumberComparison()

const roi = ref(null)
const loading = ref(true)

const currencyCode = computed(() => webSiteStore.getDefaultCurrency()?.code)
const money = (value) => {
  const code = currencyCode.value
  return code ? getCurrencyString(value ?? 0, code) : String(value ?? 0)
}
const number = (value) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value ?? 0)

const METRICS = [
  { key: 'invoices_total_year', labelKey: 'company.roi.invoicesTotalYear', format: money },
  { key: 'quotes_total_year', labelKey: 'company.roi.quotesTotalYear', format: money },
  { key: 'gross_margin_year', labelKey: 'company.roi.grossMarginYear', format: money, margin: true },
  { key: 'gross_margin_quarter', labelKey: 'company.roi.grossMarginQuarter', format: money, margin: true },
  { key: 'active_projects', labelKey: 'company.roi.activeProjects', format: number },
  { key: 'completed_projects', labelKey: 'company.roi.completedProjects', format: number },
  { key: 'avg_equipment_qty_per_project', labelKey: 'company.roi.avgEquipmentQty', format: number },
  { key: 'avg_staff_qty_per_project', labelKey: 'company.roi.avgStaffQty', format: number },
  { key: 'avg_equipment_margin', labelKey: 'company.roi.avgEquipmentMargin', format: money, margin: true },
  { key: 'avg_staff_margin', labelKey: 'company.roi.avgStaffMargin', format: money, margin: true },
]

const marginClass = (value) => {
  if (isGreater(value, 0)) return 'text-positive text-bold'
  if (isLess(value, 0)) return 'text-negative text-bold'
  return null
}

const tiles = computed(() => (roi.value ? METRICS.map(m => ({
  key: m.key,
  label: t(m.labelKey),
  value: roi.value[m.key],
  format: m.format,
  valueClass: m.margin ? marginClass(roi.value[m.key]) : null,
})) : []))

const tilePairs = computed(() => {
  const pairs = []
  for (let i = 0; i < tiles.value.length; i += 2) {
    pairs.push(tiles.value.slice(i, i + 2))
  }
  return pairs
})

onMounted(async() => {
  const { data } = await planningStore.fetchCompanyRoiByIdAsync(props.company.id)
  roi.value = data
  loading.value = false
})
</script>

<template>
  <div>
    <q-inner-loading :showing="loading" />
    <template v-if="!loading">
      <expandable-content v-if="variant === 'list'" collapsed-height="6rem">
        <div class="roi-list">
          <div v-for="tile in tiles" :key="tile.key" class="roi-list-item">
            <div class="text-caption text-grey-6">{{ tile.label }}</div>
            <div class="text-weight-medium" :class="tile.valueClass">{{ tile.format(tile.value) }}</div>
          </div>
        </div>
      </expandable-content>
      <q-list v-else-if="variant === 'info'" class="q-px-sm roi-info-list">
        <q-item v-for="(pair, index) in tilePairs" :key="index" class="roi-info-row">
          <div class="roi-info-col">
            <q-item-section>
              <q-item-label caption>{{ pair[0].label }}</q-item-label>
              <q-item-label :class="pair[0].valueClass">{{ pair[0].format(pair[0].value) }}</q-item-label>
            </q-item-section>
          </div>
          <div v-if="pair[1]" class="roi-info-col roi-info-col--right">
            <q-item-section style="display: flex; align-content: flex-end;">
              <q-item-label caption style="text-align: right;">{{ pair[1].label }}</q-item-label>
              <q-item-label style="text-align: right;" :class="pair[1].valueClass">{{ pair[1].format(pair[1].value) }}</q-item-label>
            </q-item-section>
          </div>
        </q-item>
      </q-list>
      <div v-else class="roi-grid">
        <q-card
          v-for="tile in tiles"
          :key="tile.key"
          flat
          bordered
          class="roi-tile"
        >
          <kpi-metric-tile
            :label="tile.label"
            :value="tile.value"
            :format="tile.format"
          />
        </q-card>
      </div>
    </template>
  </div>
</template>

<style scoped>
.roi-info-list {
  max-width: 28rem;
}
.roi-info-row {
  display: flex;
  gap: 1rem;
}
.roi-info-col {
  flex: 1;
  min-width: 0;
}
.roi-info-col--right {
  text-align: right;
}
.roi-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem 1rem;
  padding: 0.25rem 0.5rem;
}
.roi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(9rem, 1fr));
  gap: 0.5rem;
  padding: 0.25rem;
}
.roi-tile {
  height: 7rem;
  container-type: inline-size;
}
</style>
